<template>
  <LoadingSpinner v-if="isLoading" />

  <div v-else-if="isError" class="no-print">
    <div class="text-center mt-5">
      <h1 class="text-2xl">Error loading proposal</h1>
      <p class="text-lg">Please try again later</p>
    </div>
  </div>

  <div v-else class="no-print">
    <!-- hero section -->
    <CustomerProposalHero
      :project="project"
      :proposal="proposal"
      :is-pending="isPending"
      :preview="preview"
      @open-signature="handleOpenSignature"
      @decline="declineProposal()"
      @print="printProposal()"
      @download="download()"
      @question="gotoComment()"
    />

    <!-- floating section nav -->
    <FloatingSectionNav :sections="sectionNav" />

    <!-- proposal details -->
     <div class="w-full">
      <div
        class="max-w-screen-lg mx-2 sm:mx-4 md:mx-8 lg:mx-auto"
        :class="isAuthenticated ? '' : 'mt-10 md:mt-20'"
      >
        <!-- proposal details -->
        <section id="details" class="bg-white shadow-md md:mt-4 px-4 sm:px-6 py-8 rounded-lg text-sm">
          <!-- header informations -->
          <div class="md:hidden flex justify-start mb-4">
            <img :src="logo" alt="Uttrly" class="h-10" />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- customer informations -->
            <div>
              <!-- proposal ID -->
              <div class="mb-7">
                <h2 class="text-slate-500 inline-block">#</h2>
                <h2 class="font-bold inline-block">{{ proposal.contractId }}</h2>
              </div>

              <!-- customer informations -->
              <div class="mb-5">
                <span class="block mb-1 font-bold">{{ customer.name }}</span>
                <span class="block mb-1">{{ customer.email }}</span>
                <span
                  v-if="customer.phone"
                  class="block mb-1"
                >{{ customer.phone }}</span>
              </div>
            </div>

            <!-- log and company informations -->
            <div>
              <!-- logo, address -->
              <div class="hidden md:flex md:justify-end mb-7">
                <img
                  :src="logo"
                  alt="Uttrly"
                />
              </div>
              <div class="text-left md:text-right">
                <span class="block mb-2">11810 Grand Park Ave, Suite 500</span>
                <span class="block mb-2">North Bethesda, MD 20852</span>
                <span class="block">202.681.0825</span>
              </div>
            </div>
          </div>

          <!-- proposal date and valid until -->
          <div class="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-4 md:mt-0">
            <span>
              <b>Proposal date:</b> {{ getLocalDate(proposal.contractDate) }}
            </span>
            <span>
              <b>Valid until:</b> {{ getLocalDate(proposal.contractValidDate) }}
            </span>
          </div>

          <!-- project description -->
          <div class="mt-10">
            <h3 class="text-base font-medium leading-10 tracking-tight">Project Description:</h3>
            <p class="text-justify">{{ project.description }}</p>
          </div>


          <!-- proposal items details -->
           <!-- items -->
          <div class="border-t border-slate-300 pt-10 mt-6">
            <div>
              <h3 class="text-xl font-medium leading-10 tracking-tight">Details</h3>
            </div>

            <!-- original contract items -->
            <div class="mt-3">
              <div
                v-for="(lineItems, category, index) in getProposalItems"
                :key="category"
                class="mb-6"
              >
                <span class="font-semibold text-xl mb-3">{{ category }}</span>

                <div
                  v-for="(lineItem, idx) in lineItems"
                  :key="lineItem.id"
                  :class="[
                    idx == lineItems.length - 1 ? 'border-b border-slate-200 py-2' : 'py-2',
                    !lineItem.selected ? 'opacity-50' : ''
                  ]"
                >
                  <div>
                    <div class="grid grid-cols-1 sm:grid-cols-5 gap-2 sm:gap-4">
                      <div class="col-span-1 sm:col-span-4 flex items-start">
                        <div
                          v-if="lineItem.optional && proposal.status === PROPOSAL_STATUS.AWAITING_ACCEPTANCE"
                          class="pt-2 pr-1"
                        >
                          <!-- optional checkbox -->
                          <BaseCheckbox
                            v-model="lineItem.selected"
                            :disabled="preview"
                          />
                        </div>

                        <BaseCollapse
                          :bg-color="'bg-withe'"
                          :class="clsx(
                            'pt-0',
                            (lineItem.optional && proposal.status === PROPOSAL_STATUS.AWAITING_ACCEPTANCE) && 'pl-0',
                            (!lineItem.optional && proposal.status === PROPOSAL_STATUS.AWAITING_ACCEPTANCE) && 'pl-3',
                          )"
                          :default-open="idx === 0 && index === 0"
                        >
                          <template #header>
                            <div class="flex items-center">
                              <span class="font-bold">{{ lineItem.title }}</span>
                            </div>
                          </template>

                          <div class="text-justify" v-html="lineItem.scopeOfWork" />

                          <!-- attachments -->
                          <Lightbox :attachments="lineItem.attachments" />
                        </BaseCollapse>
                      </div>

                      <!-- totals -->
                      <div class="text-sm pt-2 font-semibold text-left sm:text-right">
                        <span>{{ currency(lineItem.totalPrice) }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- category total -->
                <div class="mt-3">
                  <div class="grid grid-cols-1 sm:grid-cols-5 gap-2 sm:gap-4">
                    <div class="col-span-1 sm:col-span-4" />
                    <div class="text-left sm:text-right">
                      <span class="font-bold">Total: </span>
                      <span>{{ currency(getCategoryTotalPrice(String(category))) }}</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <!-- proposal total price -->
            <div class="mt-6">
              <div class="grid grid-cols-1 sm:grid-cols-5 gap-2 sm:gap-4 border-t-2 border-slate-200 py-4">
                <div class="col-span-1 sm:col-span-5 text-left sm:text-right">
                  <span class="font-bold">Total: </span>
                  <span>{{ currency(proposalTotalPrice) }}</span>
                </div>
              </div>
            </div>
          </div>


          <!-- legal terms -->
          <section id="terms" class="mt-6 text-justify" v-html="proposal.termsConditions" />
        </section>


        <!-- messages -->
        <section
          id="comments"
          ref="revision-comment"
          class="mt-10 bg-white p-4 pb-10 shadow-md rounded-lg"
        >
          <h4 class="mb-4">Comments</h4>

          <!-- message thread -->
          <MessageThread
            v-if="messages"
            :key="`comments-${project.id}`"
            ref="comments"
            style="max-height: 360px; overflow-y: scroll;"
            :messages="messages"
            :message-type="MESSAGE_TYPES.COMMENT"
          />
          <MessageForm
            :project="project"
            :message-type="'comment'"
            :preview="preview"
            :save-funtion="addMessage"
          />
        </section>

      </div>
    </div>


    <Modal
      v-model="openSignatureModal"
      width="w-full md:max-w-md"
      :no-escape="true"
      :no-backdrop="true"
      :no-close-button="true"
    >
      <template #body>
        <div>
          <form @submit.stop.prevent="form.handleSubmit()">
            <h3 class="text-lg font-semibold">
              In agreement to the proposal, I hereby submit my e-signature:
            </h3>

            <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <form.Field
                name="name"
                :validators="{
                  onChange: yup.string().required('Name is required')
                }"
              >
                <template #default="{ field }">
                  <BaseInput
                    :model-value="(field.state.value as string)"
                    :name="field.name"
                    label="Name"
                    :error="field.state.meta.errors && field.state.meta.errors[0]"
                    @update:model-value="(value) => field.handleChange(String(value))"
                  />
                </template>
              </form.Field>

              <form.Field
                name="email"
                :validators="{
                  onChange: yup.string().email('Invalid email').required('Email is required')
                }"
              >
                <template #default="{ field }">
                  <BaseInput
                    :model-value="(field.state.value as string)"
                    :name="field.name"
                    label="Email"
                    type="email"
                    :error="field.state.meta.errors && field.state.meta.errors[0]"
                    @update:model-value="(value) => field.handleChange(String(value))"
                  />
                </template>
              </form.Field>
            </div>

            <div class="mt-4">
              <form.Field
                name="signature"
                :validators="{
                  onChange: yup.string().required('Signature is required')
                }"
              >
                <template #default="{ field }">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Signature
                    </label>
                    <SignaturePad
                      ref="signaturePadRef"
                      :width="450"
                      :height="150"
                      @update:signature="(value) => field.handleChange(value)"
                    />
                    <div v-if="field.state.meta.errors && field.state.meta.errors[0]" class="text-red-600 text-sm mt-1">
                      {{ field.state.meta.errors[0] }}
                    </div>
                  </div>
                </template>
              </form.Field>
            </div>

            <div class="mt-4">
              <form.Field
                name="consent"
                :validators="{
                  onChange: yup.boolean().oneOf([true], 'You must agree to the terms')
                }"
              >
                <template #default="{ field }">
                  <div class="flex items-start">
                    <BaseCheckbox
                      :model-value="(field.state.value as boolean)"
                      :name="field.name"
                      @update:model-value="(value) => field.handleChange(value)"
                    />
                    <label class="ml-2 text-sm text-gray-700">
                      I agree that by clicking "Submit E-Signature", I am electronically signing this document.
                    </label>
                  </div>
                  <div v-if="field.state.meta.errors && field.state.meta.errors[0]" class="text-red-600 text-sm mt-1 ml-6">
                    {{ field.state.meta.errors[0] }}
                  </div>
                </template>
              </form.Field>
            </div>

            <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <BaseInputDate
                v-model="proposal.signature.timestamp"
                label="Date"
                :disabled="true"
              />
              <BaseInput
                v-model="userIpAddress"
                label="IP Address"
                :disabled="true"
              />
            </div>

            <p class="mt-4 text-xs text-slate-500">
              By clicking "Submit E-Signature" button you agree to electronically sign the document presented.
            </p>

            <form.Subscribe>
              <template #default="{ canSubmit, isSubmitting }">
                <div class="flex justify-end gap-4 -mb-4 mt-6">
                  <BaseButton
                    variant="secondary"
                    type="button"
                    text="Cancel"
                    :disabled="isSubmitting"
                    :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
                    @click="closeSignatureModal"
                  />

                  <BaseButton
                    variant="primary"
                    type="submit"
                    text="Submit E-Signature"
                    :disabled="!canSubmit || isSubmitting"
                    :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
                  >
                    <span
                      v-if="isSubmitting"
                      class="i-heroicons-arrow-path animate-spin"
                    />
                  </BaseButton>
                </div>
              </template>
            </form.Subscribe>
          </form>
        </div>
      </template>
    </Modal>

    <!-- Alert Modal for No Items Selected -->
    <Modal
      v-model="openAlertModal"
      width="w-full md:max-w-md"
      :no-escape="false"
      :no-backdrop="false"
      :no-close-button="false"
    >
      <template #body>
        <div class="text-center">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">
            No Items Selected
          </h3>
          <p class="text-sm text-gray-500 mb-6">
            Please select at least one item before accepting the proposal.
          </p>
          <div class="flex justify-center">
            <BaseButton
              variant="primary"
              type="button"
              text="OK"
              @click="openAlertModal = false"
            />
          </div>
        </div>
      </template>
    </Modal>
  </div>

  <div
    v-if="!isLoading && proposal.id"
    id="printContent"
    class="hide-print-content"
  >
    <PrintProposal
      :project="project"
      :customer="customer"
      :budget="budget"
      :proposal="proposal"
    />
  </div>
  <div
    v-if="!isLoading && proposal.id"
    id="downloadProposal"
    class="no-print"
  >
    <DownloadProposal
      v-show="!pdfReady"
      :project="project"
      :customer="customer"
      :budget="budget"
      :proposal="proposal"
    />
  </div>
</template>

<script lang='ts' setup>
import { Budget } from '@/models/budget'
import { Customer } from '@/models/customer'
import { Project } from '@/models/project'
import { Proposal, PROPOSAL_STATUS } from '@/models/proposal'
import FirebaseService from '@/services/FirebaseService'
import { extend, filter, contains } from 'underscore'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseCollapse from '@/components/base/BaseCollapse.vue'
import MessageForm from '@/modules/Project/components/ProjectMessage.vue'
import MessageThread from '@/modules/Project/components/ProjectMessageThread.vue'
import { Message, MESSAGE_TYPES } from '@/models/message'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import DownloadProposal from '../components/DownloadProposal.vue'
import PrintProposal from '../components/PrintProposal.vue'
import html2pdf from 'html2pdf.js'
import logo from '@/assets/images/logo.png'
import { httpsCallable } from 'firebase/functions'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useRoute } from 'vue-router'
import { computed, ref, useTemplateRef, watch, onMounted } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { useHead } from '@unhead/vue'
import { currency, sortLineItemsByCategoryName, toNumber } from '@/utils'
import ProjectService from '@/services/ProjectService'
import Modal from '@/components/app/AppModal.vue'
import BaseInputDate from '@/components/base/BaseInputDate.vue'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import clsx from 'clsx'
// import LazyImage from '@/components/LazyImage.vue'
import Lightbox from '@/components/app/AppLightBox.vue'
import { useAuth } from '@/composables/useAuth'
import CustomerProposalHero from '../components/CustomerProposalHero.vue'
import FloatingSectionNav from '../components/FloatingSectionNav.vue'
import SignaturePad from '@/components/SignaturePad.vue'
import { getClientIpAddress } from '@/utils/ipAddress'
import { AUDIT_ACTION_TYPES } from '@/models/proposalAudit'

useHead({ title: 'Proposal' })

const { isAuthenticated } = useAuth()
const { notify } = useNotification()
const $route = useRoute()
const id = $route.params.id as string
const preview = $route.query.preview === 'true' || false

const project = ref(new Project())
const customer = ref(new Customer())
const budget = ref(new Budget())
const proposal = ref({
  ...new Proposal(),
  signature: {
    name: '',
    email: '',
    timestamp: new Date(),
    consent: false,
    ipAddress: '',
    signature: '',
    signatureImage: '',
    date: new Date()
  }
})

const { data: fetchedData, isLoading, isError } = useQuery({
  queryKey: ['customer/proposals', id],
  queryFn: async () => {
    const functions = await FirebaseService.functions()
    const fetchData = httpsCallable(functions, 'proposal-fetchProposalDataV2')
    const response = await fetchData({ id, preview })

    try {
      const data = response.data as any
      project.value = extend(project.value, JSON.parse(JSON.stringify(data.project))) as Project
      budget.value = extend(budget.value, JSON.parse(JSON.stringify(data.budget))) as Budget
      proposal.value = extend(proposal.value, JSON.parse(JSON.stringify(data.proposal))) as Proposal
      customer.value = extend(customer.value, JSON.parse(JSON.stringify(data.customer))) as Customer

      return { project: project.value, budget: budget.value, proposal: proposal.value, customer: customer.value }
    } catch (err) {
      console.error(err)
      notify({
        type: 'error',
        title: 'Error',
        text: 'Error trying to fetch data'
      })
    }
  }
})


watch(isLoading, (loading) => {
  if (!loading && !isError.value) {
    budget.value = extend(new Budget(), JSON.parse(JSON.stringify(fetchedData.value.budget))) as Budget
    project.value = extend(new Project(), JSON.parse(JSON.stringify(fetchedData.value.project))) as Project
    proposal.value = extend({
      ...new Proposal(),
      signature: {
        name: '',
        email: '',
        timestamp: new Date(),
        consent: false,
        ipAddress: '',
        signature: '',
        signatureImage: '',
        date: new Date()
      }
    }, JSON.parse(JSON.stringify(fetchedData.value.proposal))) as Proposal
    customer.value = extend(new Customer(), JSON.parse(JSON.stringify(fetchedData.value.customer))) as Customer

    
  }
}, { immediate: true })

const enable = computed(() => !!project.value.id)
const projectService = new ProjectService()
const queryClient = useQueryClient()

// get customer messages
const { data: messages } = useQuery({
  queryKey: ['messages', id],
  queryFn: async () => {
    try {
      const functions = await FirebaseService.functions()
      const fetchMessages = httpsCallable(functions, 'project-getProjectMessagesV2')
      const response = await fetchMessages({ projectId: project.value.id, customerId: project.value.customerId }) as any

      return response.data.messages
    } catch (error) {
      console.error(error, 'error customer messages')
      notify({
        type: 'error',
        title: 'Error',
        text: 'Error trying to fetch messages'
      })
      return []
    }
  },
  enabled: enable
})

const messageThreadRef = useTemplateRef('comments')
const { mutateAsync: addMessage } = useMutation({
  mutationFn: async (message: Message) => {
    if (!message.from.id) {
      message.from.id = project.value.customerId
      message.from.email = customer.value.email
      message.from.name = customer.value.name
    }

    const newMessage = await projectService.addMessageToProject(project.value.id, project.value.customerId, message)
    return newMessage
  },
  onSuccess: (newMessage) => {
    queryClient.setQueryData(['messages', project.value.id], [...messages.value, newMessage])

    notify({
      type: 'success',
      title: 'Message sent',
      text: 'Message sent successfully'
    })

    setTimeout(() => {
      messageThreadRef.value.$el.scrollTo({
        top: messageThreadRef.value.$el.scrollHeight,
        behavior: 'smooth'
      })
    }, 100)
  },
  onError: (error) => {
    console.error(error)
    notify({
      type: 'error',
      title: 'Error',
      text: 'Error while trying to send message'
    })
  }
})

const getLocalDate = computed(() => (date: Date) => {
  return new Date(date).toLocaleDateString()
})

const getProposalItems = computed(() => {
  const items = filter(budget.value.lineItems, (item) => contains(proposal.value.lineItems, item.id))
  const sortedItems = sortLineItemsByCategoryName(items)

  return sortedItems
})

const getCategoryTotalPrice = computed(() => (category: string) => {
  return budget.value.lineItems
    .filter(item => item.category.name === category && proposal.value.lineItems.includes(item.id) && item.selected)
    .reduce((total, item) => total + toNumber(item.totalPrice), 0)
})

const proposalTotalPrice = computed(() => {
  return budget.value.lineItems
    .filter(item => proposal.value.lineItems.includes(item.id) && item.selected)
    .reduce((total, item) => total + toNumber(item.totalPrice), 0)
})


const { mutateAsync: declineProposal, isPending: decliningProposal } = useMutation({
  mutationFn: async () => {
    try {
      const functions = await FirebaseService.functions()
      const decline = httpsCallable(functions, 'proposal-declineProposalV2')

      proposal.value.status = PROPOSAL_STATUS.DECLINED
      await decline({ proposal: proposal.value, proposalTotal: proposalTotalPrice.value })
      
      // Update query cache to reflect the change
      queryClient.setQueryData(['customer/proposals', id], (oldData: any) => {
        if (oldData) {
          return {
            ...oldData,
            proposal: { ...oldData.proposal, status: PROPOSAL_STATUS.DECLINED }
          }
        }
        return oldData
      })
    } catch (_) {
      proposal.value.status = PROPOSAL_STATUS.AWAITING_ACCEPTANCE
    }
  },
  onSuccess: () => {
    notify({
      type: 'success',
      title: 'Proposal declined',
      text: 'Proposal declined successfully'
    })
    
    // Ensure the UI updates immediately
    queryClient.invalidateQueries({ queryKey: ['customer/proposals', id] })
  },
  onError: () => {
    notify({
      type: 'error',
      title: 'Error',
      text: 'Error while trying to decline proposal'
    })
  }
})

const openSignatureModal = ref(false)
const openAlertModal = ref(false)
const userIpAddress = ref('Loading...')
const signaturePadRef = ref<InstanceType<typeof SignaturePad>>()

const handleOpenSignature = () => {
  // Check if proposal total is 0 (no items selected)
  if (proposalTotalPrice.value === 0) {
    openAlertModal.value = true
    return
  }
  
  openSignatureModal.value = true
}

onMounted(async () => {
  userIpAddress.value = await getClientIpAddress()
})

// Watch for proposal data to be loaded to log view action
watch(() => proposal.value.id, async (newId) => {
  if (newId && project.value.id && userIpAddress.value !== 'Loading...' && userIpAddress.value !== 'Unknown') {
    try {
      // Log view action when proposal is loaded
      if (proposal.value.id && project.value.id) {
        const functions = await FirebaseService.functions()
        const logAudit = httpsCallable(functions, 'proposal-logProposalAudit')

        await logAudit({
          auditData: {
            proposalId: proposal.value.id,
            projectId: project.value.id,
            action: AUDIT_ACTION_TYPES.VIEWED,
            userId: customer.value.id || undefined,
            userEmail: customer.value.email || undefined,
            userName: customer.value.name || undefined,
            ipAddress: userIpAddress.value,
            userAgent: navigator.userAgent,
            details: {
              viewedAt: new Date().toISOString()
            }
          }
        })
      }
    } catch (error) {
      console.error('Error logging view action:', error)
      userIpAddress.value = 'Unknown'
      notify({
          type: 'error',
          title: 'Error',
          text: 'Could not log view event.'
        })
    }
  }
})

const closeSignatureModal = () => {
  form.reset()
  signaturePadRef.value?.clear()
  openSignatureModal.value = false
}

const form = useForm({
  defaultValues: {
    name: proposal.value.signature.name || '',
    email: proposal.value.signature.email || customer.value.email || '',
    signature: '',
    consent: false
  },
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    // Check if proposal total is 0 (no items selected)
    if (proposalTotalPrice.value === 0) {
      notify({
        type: 'error',
        title: 'No Items Selected',
        text: 'Please select at least one item before accepting the proposal.'
      })
      return
    }

    proposal.value.signature.name = value.name
    proposal.value.signature.email = value.email
    proposal.value.signature.consent = value.consent
    proposal.value.signature.ipAddress = userIpAddress.value
    proposal.value.signature.date = new Date()
    proposal.value.signature.timestamp = new Date()

    if (signaturePadRef.value) {
      const dataUrl = signaturePadRef.value.toDataURL()
      if (dataUrl) {
        try {
          const functions = await FirebaseService.functions()
          const uploadAndLog = httpsCallable(functions, 'proposal-uploadSignatureAndLogAudit')
          
          const auditPayload = {
            proposalId: proposal.value.id,
            projectId: project.value.id,
            action: AUDIT_ACTION_TYPES.SIGNED,
            userEmail: proposal.value.signature.email,
            userName: proposal.value.signature.name,
            ipAddress: userIpAddress.value,
            userAgent: navigator.userAgent,
            details: {
              consent: proposal.value.signature.consent,
              timestamp: proposal.value.signature.timestamp.toISOString()
            }
          }

          const response = await uploadAndLog({
            proposalId: proposal.value.id,
            projectId: project.value.id,
            fileDataUrl: dataUrl,
            auditData: auditPayload
          }) as any

          if (response.data && response.data.signatureImageUrl) {
            proposal.value.signature.signatureImage = response.data.signatureImageUrl
          } else {
            throw new Error('Signature image URL not returned from function.')
          }

        } catch (error) {
          console.error('Error uploading signature and logging audit:', error)
          notify({
            type: 'error',
            title: 'Signature Error',
            text: 'Failed to save signature or log action. Please try again.'
          })
          return
        }
      }
    }

    await acceptProposal()

    openSignatureModal.value = false
    
    // Clear the signature form
    form.reset()
    signaturePadRef.value?.clear()
  }
})

const { mutateAsync: acceptProposal, isPending: acceptingProposal } = useMutation({
  mutationFn: async () => {
    try {
      const functions = await FirebaseService.functions()
      const accept = httpsCallable(functions, 'proposal-acceptProposalV2')
      const updatedProposal = extend({}, proposal.value) as Proposal

      updatedProposal.lineItems = budget.value.lineItems
        .filter(item => proposal.value.lineItems.includes(item.id) && item.selected)
        .map(item => item.id)

        updatedProposal.status = PROPOSAL_STATUS.ACCEPTED
      await accept({ proposal: updatedProposal, budget: budget.value, proposalTotal: proposalTotalPrice.value })
      proposal.value = updatedProposal
      
      // Update query cache to reflect the change
      queryClient.setQueryData(['customer/proposals', id], (oldData: any) => {
        if (oldData) {
          return {
            ...oldData,
            proposal: { ...oldData.proposal, status: PROPOSAL_STATUS.ACCEPTED, signature: proposal.value.signature }
          }
        }
        return oldData
      })
    } catch (_) {
      proposal.value.status = PROPOSAL_STATUS.AWAITING_ACCEPTANCE
    }
  },
  onSuccess: () => {
    notify({
      type: 'success',
      title: 'Proposal accepted',
      text: 'Proposal accepted successfully'
    })
    
    // Ensure the UI updates immediately
    queryClient.invalidateQueries({ queryKey: ['customer/proposals', id] })
  },
  onError: () => {
    notify({
      type: 'error',
      title: 'Error',
      text: 'Error while trying to accept proposal'
    })
  }
})

const isPending = computed(() => decliningProposal.value || acceptingProposal.value)

const revisionCommentRef = useTemplateRef('revision-comment')
const gotoComment = () => {
  const element = revisionCommentRef.value as HTMLElement
  if (element) {
    const top = element.offsetTop

    window.scrollTo({
      top,
      behavior: 'smooth'
    })
  }
}

const printProposal = () => {
  window.print()
}

const pdfReady = ref(true)
const download = async () => {
  pdfReady.value = false
  const element = document.getElementById('downloadProposal')
  const options = {
    margin: 0.5,
    filename: `proposal-${project.value.name}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
  }

  html2pdf().from(element).set(options).save().then(() => {
    pdfReady.value = true
  })
}

// floating nav sections
const sectionNav: Array<{ id: string; label: string }> = [
  { id: 'intro', label: 'Intro' },
  { id: 'details', label: 'Details' },
  { id: 'terms', label: 'Terms' },
  { id: 'comments', label: 'Comments' }
]
</script>

<style lang="scss" scoped>
.not-selected-item {
  opacity: 0.6;
}

@media print {
  .no-print {
    display: none;
  }
  #printContent {
    display: block;
  }
}

.hide-print-content {
  display: none;
}
</style>
